import { useEffect } from "react"
import { createFileRoute, Link } from "@tanstack/react-router"
import { ArrowLeft, ArrowUpRight, FileQuestion } from "lucide-react"
import { usePage } from "@/hooks/usePages"
import { pageMeta, syncMetaTags } from "@/lib/seo"
import { JsonLd } from "@/components/json-ld"

function titleCase(slug: string) {
  return slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
}

/**
 * Catch-all for custom pages created in the admin (Pages module) and linked
 * to from the Menu, e.g. a "Test" menu item pointing at "/test". This file
 * sits directly under `_public`, as a sibling to the other static routes
 * (`_public.about.tsx`, `_public.contact.tsx`, ...) — TanStack Router
 * matches those exact segments first, so this dynamic `$slug` route only
 * ever catches paths nothing more specific already claims. Any slug that
 * doesn't correspond to a published page (typo, unpublished draft, never
 * existed) falls through to the "not found" state below, same as a normal
 * 404 would.
 */
export const Route = createFileRoute("/_public/$slug")({
  component: CustomPage,
  head: ({ params }) =>
    pageMeta({
      title: `${titleCase(params.slug)} — Nexovia IT`,
      description: "Nexovia IT — software design and engineering.",
      path: `/${params.slug}`,
    }),
})

function CustomPage() {
  const { slug } = Route.useParams()
  const { data: page, isLoading, isError } = usePage(slug)

  useEffect(() => {
    if (!page) return
    syncMetaTags({
      title: `${page.title} — Nexovia IT`,
      description: page.excerpt ?? undefined,
    })
  }, [page])

  if (isLoading) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-20 md:py-24 space-y-4">
        <div className="h-4 w-1/4 animate-pulse rounded bg-secondary" />
        <div className="h-10 w-3/4 animate-pulse rounded bg-secondary" />
        <div className="h-4 w-full animate-pulse rounded bg-secondary" />
        <div className="h-4 w-5/6 animate-pulse rounded bg-secondary" />
      </div>
    )
  }

  if (isError || !page) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-24 md:py-28 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-secondary">
          <FileQuestion className="h-7 w-7 text-muted-foreground" />
        </div>
        <h1 className="mt-6 text-3xl md:text-4xl font-bold">Page not found.</h1>
        <p className="mx-auto mt-3 max-w-md text-muted-foreground">
          The page you're looking for doesn't exist or may have been unpublished.
        </p>
        <div className="mt-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-full bg-gradient-accent px-6 py-3 text-sm font-semibold text-cream shadow-elegant"
          >
            <ArrowLeft className="h-4 w-4" /> Back home
          </Link>
        </div>
      </div>
    )
  }

  const pageUrl = typeof window !== "undefined" ? window.location.href : ""

  return (
    <>
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: page.title,
          ...(page.excerpt && { description: page.excerpt }),
          url: pageUrl,
        }}
      />

      <section className="border-b border-border bg-background">
        <div className="mx-auto max-w-4xl px-6 py-16 md:py-20 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-balance">{page.title}</h1>
          {page.excerpt && (
            <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">{page.excerpt}</p>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 py-16 md:py-20">
        {page.content ? (
          <div
            className="prose prose-neutral max-w-none prose-headings:font-display prose-a:text-rust"
            dangerouslySetInnerHTML={{ __html: page.content }}
          />
        ) : (
          <p className="text-center text-muted-foreground">Content coming soon.</p>
        )}
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-24 md:pb-28">
        <div className="rounded-3xl border border-border bg-card p-10 md:p-16 text-center shadow-card">
          <p className="text-xs font-semibold uppercase tracking-widest text-rust">Work with us</p>
          <h2 className="mt-3 text-3xl md:text-4xl font-bold">Let's Build Something Great.</h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-accent px-6 py-3 text-sm font-semibold text-cream shadow-elegant"
            >
              Get in touch <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  )
}
